/**
 * Version check utility to force PWA updates
 * Checks for new versions and prompts user to reload
 */

import { storage } from './storage'
import { createLogger } from './logger'

const logger = createLogger('VersionCheck')

const VERSION_CHECK_INTERVAL = 10000 // Check every 10 seconds (aggressive)
const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.1' // Bump version to force update

let versionCheckInterval = null

/**
 * Check if a new version is available by comparing build hashes
 */
export async function checkForNewVersion() {
  try {
    // Fetch the index.html with cache-busting
    const response = await fetch(`/index.html?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache'
      }
    })
    
    const html = await response.text()
    
    // Extract the main JS bundle name from the HTML
    const match = html.match(/assets\/index-([a-f0-9]+)\.js/)
    
    if (match) {
      const newHash = match[1]
      const currentHash = getCurrentBuildHash()
      
      // If hashes differ, we have a new version
      if (currentHash && newHash !== currentHash) {
        logger.info('New version detected', { newHash, currentHash })
        return true
      }
      
      // Store current hash for future checks
      if (!currentHash) {
        storage.set('buildHash', newHash)
      }
    }
    
    return false
  } catch (error) {
    logger.error('Version check failed', error)
    return false
  }
}

/**
 * Get the current build hash from storage or script tags
 */
function getCurrentBuildHash() {
  // Try storage first
  const stored = storage.get('buildHash')
  if (stored) return stored
  
  // Extract from current page's script tags
  const scripts = document.querySelectorAll('script[src*="assets/index-"]')
  for (const script of scripts) {
    const match = script.src.match(/assets\/index-([a-f0-9]+)\.js/)
    if (match) {
      const hash = match[1]
      storage.set('buildHash', hash)
      return hash
    }
  }
  
  return null
}

/**
 * Start periodic version checking
 */
export function startVersionCheck() {
  // Check immediately on start
  checkForNewVersion().then(hasNewVersion => {
    if (hasNewVersion) {
      promptForUpdate()
    }
  })
  
  // Then check periodically
  versionCheckInterval = setInterval(async () => {
    const hasNewVersion = await checkForNewVersion()
    if (hasNewVersion) {
      promptForUpdate()
    }
  }, VERSION_CHECK_INTERVAL)
}

/**
 * Stop version checking
 */
export function stopVersionCheck() {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval)
    versionCheckInterval = null
  }
}

/**
 * Prompt user to update to new version
 */
function promptForUpdate() {
  // Always prompt - don't cache dismissals for critical updates
  const lastPrompt = storage.get('lastUpdatePrompt')
  const now = Date.now()
  
  // Only throttle to once per minute to avoid spam
  if (lastPrompt && (now - parseInt(lastPrompt)) < 60000) {
    return
  }
  
  storage.set('lastUpdatePrompt', now)
  
  logger.info('New version detected — forcing reload')
  forceUpdate()
}

/**
 * Force update by clearing caches and reloading
 */
export async function forceUpdate() {
  try {
    logger.info('Forcing update: clearing all caches')
    
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      logger.info('Unregistering service workers', { count: registrations.length })
      await Promise.all(registrations.map(reg => reg.unregister()))
    }
    
    // Clear ALL caches (PWA + browser cache)
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      logger.info('Deleting caches', { count: cacheNames.length })
      await Promise.all(cacheNames.map(name => caches.delete(name)))
    }
    
    // Clear all version-related storage items
    storage.remove('buildHash')
    storage.remove('updatePromptShown')
    storage.remove('lastUpdatePrompt')
    
    // Add cache-busting query parameter and hard reload
    const url = new URL(window.location.href)
    url.searchParams.set('v', Date.now().toString())
    window.location.href = url.toString()
  } catch (error) {
    logger.error('Force update failed', error)
    // Fallback to simple reload with cache bust
    window.location.href = window.location.href + '?v=' + Date.now()
  }
}

/**
 * Initialize version checking on app start
 */
export function initVersionCheck() {
  // Only run in production
  if (import.meta.env.DEV) {
    return
  }
  
  logger.info('Version check enabled - checking for updates')
  
  // Start checking immediately for faster update detection
  setTimeout(() => {
    startVersionCheck()
  }, 1000) // Wait only 1 second after app loads
  
  // Clean up on page unload
  window.addEventListener('beforeunload', stopVersionCheck)
}
